import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ClientList from './pages/ClientList';
import ClientWizard from './pages/ClientWizard';
import ClientFiche from './pages/ClientFiche';
import CommandeDetail from './pages/CommandeDetail';
import CommandesList from './pages/CommandesList';
import Livraisons from './pages/Livraisons';
import InterventionDetail from './pages/InterventionDetail';
import ConfirmationPage from './pages/ConfirmationPage';
import LivreurPage from './pages/LivreurPage';
import Settings from './pages/Settings';
import Produits from './pages/Produits';
import SuiviCarsat from './pages/SuiviCarsat';
import MonitoringEmail from './pages/MonitoringEmail';
import GestionEmail from './pages/GestionEmail';
import Layout from './components/Layout';
import './App.css';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(222,47%,11%)]"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'livreur') return <Navigate to="/livreur" replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
}

function LivreurRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(222,47%,11%)]"></div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RedirectAfterLogin() {
  const { user } = useAuth();
  if (!user) return <Login />;
  if (user.role === 'livreur') return <Navigate to="/livreur" replace />;
  return <Navigate to="/" replace />;
}

function CatchAllRedirect() {
  const { user } = useAuth();
  if (user?.role === 'livreur') return <Navigate to="/livreur" replace />;
  return <Navigate to="/" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<RedirectAfterLogin />} />
      <Route path="/confirmation/:token" element={<ConfirmationPage />} />
      <Route path="/livreur" element={<LivreurRoute><LivreurPage /></LivreurRoute>} />
      <Route path="/" element={<ProtectedRoute allowedRoles={['admin','commercial']}><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="clients" element={<ClientList />} />
        <Route path="clients/nouveau" element={<ClientWizard />} />
        <Route path="clients/:id" element={<ClientFiche />} />
        <Route path="commandes" element={<CommandesList />} />
        <Route path="commandes/:id" element={<CommandeDetail />} />
        <Route path="livraisons" element={<Livraisons />} />
        <Route path="interventions/:id" element={<InterventionDetail />} />
        <Route path="parametres" element={<ProtectedRoute allowedRoles={['admin']}><Settings /></ProtectedRoute>} />
        <Route path="produits" element={<ProtectedRoute allowedRoles={['admin']}><Produits /></ProtectedRoute>} />
        <Route path="suivi-carsat" element={<SuiviCarsat />} />
        <Route path="monitoring-email" element={<ProtectedRoute allowedRoles={['admin']}><MonitoringEmail /></ProtectedRoute>} />
        <Route path="messagerie" element={<ProtectedRoute allowedRoles={['admin']}><GestionEmail /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<CatchAllRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
